/* The sticky breadcrumb.
 *
 * Replaces the unit rail. Every segment names where you are at one level and
 * opens a menu of that level's SIBLINGS — unit ⌄ › lesson ⌄ › section ⌄ — the
 * way a code editor's breadcrumb does. A segment that lists children instead is
 * the trap this started out in: the unit segment offered the unit's lessons,
 * which made the lesson segment redundant and left no way at all to reach
 * another unit without going back out to the course home.
 *
 * A segment with nothing to switch to renders as a plain label rather than a
 * dead control. That is also the rule that makes a course segment honest the
 * day a second course has content: same component, no special case.
 *
 * Why this and not the rail: a full-bleed band cannot run full width with a
 * 280px column beside it, and a fixed rail spends a permanent quarter of the
 * screen on navigation the student uses twice a session. The breadcrumb gives
 * back the width, and adds in-lesson jumping the rail never had.
 *
 * The section segment tracks scroll, so "where am I" survives a long page —
 * which is the one thing the rail was genuinely good at.
 */

import { useState, useEffect, useRef } from 'react';
import s from './LessonNav.module.css';
import { unitName } from '../content/course.js';

const cx = (...v) => v.filter(Boolean).join(' ');

function Menu({ label, items, currentId, onPick, align = 'left', current }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const away = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  /* One sibling is no choice. Render the label, not a caret that does nothing. */
  if (items.length < 2) {
    return <span className={cx(s.segStatic, current && s.segStaticCurrent)}>{label}</span>;
  }

  return (
    <span className={s.segWrap} ref={ref}>
      <button type="button" className={cx(s.seg, current && s.segCurrent, open && s.segOpen)}
              aria-expanded={open} aria-haspopup="menu"
              onClick={() => setOpen(o => !o)}>
        <span className={s.segLabel}>{label}</span>
        <svg className={s.caret} width="9" height="6" viewBox="0 0 9 6" aria-hidden="true">
          <path d="M1 1.5 4.5 5 8 1.5" fill="none" stroke="currentColor" strokeWidth="1.4"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className={cx(s.menu, align === 'right' && s.menuRight)} role="menu">
          {items.map(it => (
            <button key={it.id} type="button" role="menuitem"
                    className={cx(s.item,
                                  it.id === currentId && s.itemCurrent,
                                  /* where you already are is not "locked" */
                                  it.disabled && it.id !== currentId && s.itemLocked)}
                    disabled={it.disabled}
                    onClick={() => { setOpen(false); onPick(it); }}>
              <span className={s.itemLabel}>{it.label}</span>
              {it.meta && <span className={s.itemMeta}>{it.meta}</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

const Sep = () => (
  <svg className={s.sep} width="7" height="12" viewBox="0 0 7 12" aria-hidden="true">
    <path d="M1 1l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/** The unit's meta, narrowed for a menu column.

    Kept short: the meta is nowrap, so anything long squeezes the unit's name
    onto two lines. Units no longer carry a week (D-038), so the meta is already
    the short form. */
const menuMeta = u => u.meta;

/** The lesson a unit should open on: the one in progress, else the first that
    is not locked. A unit with neither cannot be entered, so it is not offered. */
const entryOf = u =>
  u.lessons.find(l => l.status === 'active') ||
  u.lessons.find(l => l.status !== 'locked') ||
  null;

export default function LessonNav({ lesson, unit, units = [], sections, onOpen, onHome, style }) {
  const [current, setCurrent] = useState(null);

  /* Scroll-spy: the section whose heading is nearest above the reading line.
     Driven off scroll rather than IntersectionObserver so a section taller
     than the viewport still reports as current while you are inside it. */
  useEffect(() => {
    if (!sections.length) return;
    let frame = null;
    const measure = () => {
      frame = null;
      const line = 140;                       // just under the sticky bar
      let found = null;
      for (const sec of sections) {
        const el = document.getElementById(sec.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) found = sec;
        else break;
      }
      setCurrent(found ? found.id : null);
    };
    const onScroll = () => { if (frame === null) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [sections]);

  const jump = it => {
    const el = document.getElementById(it.id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const here = sections.find(x => x.id === current);
  const siblingUnits = units.length ? units : [unit];

  return (
    <div className={s.bar} style={style}>
      <div className={s.inner}>
        {/* level 0 — the way out. A breadcrumb whose first segment is a unit
            has no root: when only one week is published that segment is dead
            text, and the only way back to the course was a small link in the
            far corner of the top bar. Every lesson-scoped page gets this. */}
        <button type="button" className={s.home} onClick={() => onHome?.()}>
          <span className={s.homeArrow} aria-hidden="true">←</span>
          <span>Course</span>
        </button>
        <Sep />

        {/* level 1 — the unit, listing the other units */}
        <Menu
          label={unitName(unit)}
          items={siblingUnits.map(u => ({
            id: u.id, label: unitName(u), meta: menuMeta(u),
            disabled: u.id === unit.id || !entryOf(u),
          }))}
          currentId={unit.id}
          onPick={it => {
            const u = siblingUnits.find(x => x.id === it.id);
            const target = u && entryOf(u);
            if (target) onOpen?.(target);
          }}
        />
        <Sep />

        {/* level 2 — the lesson, listing the other lessons in this unit */}
        <Menu
          label={lesson.title}
          current
          items={unit.lessons.map(l => ({
            id: l.id, label: l.title,
            disabled: l.status === 'locked' || l.id === lesson.id,
          }))}
          currentId={lesson.id}
          onPick={it => onOpen?.(unit.lessons.find(l => l.id === it.id))}
        />

        {/* level 3 — the section, listing the other sections in this lesson */}
        {sections.length > 0 && (
          <>
            <Sep />
            <Menu
              label={here ? here.label : 'Jump to a section'}
              items={sections}
              currentId={current}
              onPick={jump}
            />
          </>
        )}

        <span className={s.spacer} />
        <span className={s.meta}>{unit.meta}</span>
      </div>
    </div>
  );
}
